import axios from 'axios';
import { clearOnboardingCache } from './onboardingCache';

// Create axios instance
const api = axios.create({
  baseURL: (import.meta as any).env.VITE_API_URL || '/api',
})

// Add auth token and impersonation parameter to requests
api.interceptors.request.use(async (config) => {
  if (config.data instanceof FormData) {
    const headers = config.headers as any;
    headers?.delete?.('Content-Type');
    headers?.delete?.('content-type');
    if (headers) {
      delete headers['Content-Type'];
      delete headers['content-type'];
    }
  }

  try {
    // Wait for Clerk to be fully loaded before attempting to get token
    if (!(window as any).Clerk) {
      console.warn('🔑 Clerk not yet initialized, API call may fail');
    } else {
      const token = await (window as any).Clerk.session?.getToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      } else {
        console.warn('🔑 No Clerk session token available');
      }
    }
  } catch (error) {
    console.error('🔑 Error getting Clerk token:', error);
  }
  
  // Add impersonation and ghost mode parameters if present in URL
  const urlParams = new URLSearchParams(window.location.search);
  const impersonateId = urlParams.get('impersonate');
  const ghostOrgId = urlParams.get('org');
  const ghostSessionId = urlParams.get('ghost') || urlParams.get('ghostSession');
  
  if (impersonateId) {
    // Add to query parameters
    config.params = {
      ...config.params,
      impersonate: impersonateId
    };
  }
  
  if (ghostOrgId) {
    // Add ghost mode organization parameter
    config.params = {
      ...config.params,
      org: ghostOrgId,
      ...(ghostSessionId ? { ghost: ghostSessionId } : {})
    };
  }
  
  
  return config
})

// Handle response errors and cache clearing
api.interceptors.response.use(
  (response) => {
    // Check for cache clear header
    if (response.headers['x-clear-onboarding-cache'] === 'true') {
      clearOnboardingCache();
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Check for impersonation parameters to avoid redirect during impersonation
      const urlParams = new URLSearchParams(window.location.search);
      const impersonateId = urlParams.get('impersonate');
      const ghostOrgId = urlParams.get('org');
      
      if (impersonateId || ghostOrgId) {
        // During impersonation, don't redirect on 401 errors
        return Promise.reject(error);
      }
      
      // Don't redirect if we're already on sign-in page to prevent infinite loops
      if (!window.location.pathname.includes('/sign-in')) {
        // Redirect to sign in
        window.location.href = '/sign-in'
      }
    }
    return Promise.reject(error)
  }
)

export { api }
